import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment } from 'react'
import {
    Body,
    ButtonAction,
    ButtonsContainer,
    ContentContainer,
    dialogPanel,
    dialogTitle
} from './styles'
import { IoCloseSharp } from 'react-icons/io5'
import exclamation from '../../../assets/svg/exclamationmark.svg'

interface ReopenNotificationModalProps {
    handleCloseModal: () => void
    isOpen: boolean
    handleReopenNotification: () => void
    isGroupTreatment?: boolean
    isLoading?: boolean
    notificationStatusState?: string
}

const ReopenNotificationModal: React.FC<ReopenNotificationModalProps> = ({
    handleCloseModal,
    isOpen,
    handleReopenNotification,
    isGroupTreatment = false,
    isLoading = false,
    notificationStatusState = ''
}) => {
    const title = isGroupTreatment ? 'Reabrir tratamento em grupo' : 'Reabrir notificação'
    
    // Verificar se está arquivada (apenas para notificações individuais, grupos não podem ser arquivados)
    const isArchived = !isGroupTreatment && notificationStatusState === 'Arquivada'
    
    // Mensagem condicional: se arquivada, menciona status "Nova", senão mantém "em andamento"
    // Para grupos, sempre mostra "em andamento" pois grupos não podem ser arquivados
    const message = isGroupTreatment 
        ? 'Tem certeza que deseja reabrir o tratamento em grupo? Todas as notificações do grupo voltarão para o status em andamento e ficarão em nome da pessoa que a finalizou.'
        : isArchived
            ? 'Tem certeza que deseja reabrir esta notificação? Ela voltará para o status Nova e a justificativa da arquivação será apagada.'
            : 'Tem certeza que deseja reabrir esta notificação? Ela voltará para o status em andamento e ficará em nome da pessoa que a finalizou.'
    
    const buttonText = isGroupTreatment ? 'Sim, reabrir tratamento em grupo' : 'Sim, reabrir notificação'

    return (
        <>
            <Transition
                appear
                show={isOpen}
                as={Fragment}
                data-testid="test-ReopenNotificationModal"
            >
                <Dialog
                    as="div"
                    className="relative z-10"
                    onClose={() => { if (!isLoading) handleCloseModal() }}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className={dialogPanel}>
                                    <ContentContainer>
                                        <div className="flex flex-col items-center text-center">
                                            <img 
                                                src={exclamation} 
                                                alt="Aviso" 
                                                className="w-16 h-16 mb-4"
                                            />
                                            
                                            <h2 className={dialogTitle} style={{ color: '#F0B323' }}>
                                                {title}
                                            </h2>
                                            
                                            <Body>
                                                <p className="mb-3">
                                                    {message}
                                                </p>
                                                <p>
                                                    Não será possível desfazer essa ação.
                                                </p>
                                            </Body>
                                            
                                            <ButtonsContainer>
                                                <ButtonAction
                                                    onClick={handleCloseModal}
                                                    type="button"
                                                    disabled={isLoading}
                                                    className="text-blueTheme border border-blueTheme bg-white hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    Cancelar
                                                </ButtonAction>
                                                <ButtonAction
                                                    onClick={handleReopenNotification}
                                                    type="button"
                                                    disabled={isLoading}
                                                    className="bg-blueTheme text-white hover:bg-blue-700 disabled:opacity-50"
                                                >
                                                    {isLoading ? 'Reabrindo...' : buttonText}
                                                </ButtonAction>
                                            </ButtonsContainer>
                                        </div>
                                    </ContentContainer>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    )
}

export { ReopenNotificationModal }
